import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  fetchMaterialStructureImport,
  startMaterialStructureImport,
} from "#modules/materials/infra/materialStructureImport.api";
import { ApiError } from "#shared/infra/apiError";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { MaterialStructureImportResult } from "shared";

const POLLING_INTERVAL_MS = 2000;

const toStartFailure = (error: unknown): MaterialStructureImportResult => ({
  outcome: "failed",
  failureCode: error instanceof ApiError && error.status === 400 ? "UNREADABLE_FILE" : "JOB_FAILED",
  missingFields: [],
  errors: [],
});

export const useMaterialStructureImport = ({
  onFinished,
}: {
  onFinished: (result: MaterialStructureImportResult) => void;
}) => {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const { mutate, isPending } = useMutation({
    mutationFn: startMaterialStructureImport,
    onSuccess: (response) => setJobId(response.jobId),
    onError: (error) => onFinishedRef.current(toStartFailure(error)),
  });

  const { data: materialStructureImport, isError } = useQuery({
    queryKey: queryKeys.materials.structureImport(jobId ?? ""),
    queryFn: () => fetchMaterialStructureImport(jobId!),
    enabled: jobId !== null,
    refetchInterval: (query) => (query.state.data?.result ? false : POLLING_INTERVAL_MS),
  });

  const result = materialStructureImport?.result;

  useEffect(() => {
    if (!jobId || (!result && !isError)) {
      return;
    }

    setJobId(null);
    void queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
    onFinishedRef.current(result ?? toStartFailure(null));
  }, [jobId, result, isError, queryClient]);

  return {
    importMaterialList: (file: File) => mutate(file),
    isImporting: isPending || jobId !== null,
  };
};
